import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { callDataTool, type DataToolResult } from "../../api/hub/open";
import {
  getSelectedTenantId,
  getStoredTenant,
  getStoredUser,
} from "../../api/session";
import type { Role } from "../../api/hub/types";
export type WorkspaceMe = {
  id: string;
  name: string;
  role: Role;
  tenantId: string;
  timezone: string;
};
export function homeScope() {
  const user = getStoredUser();
  return [
    getSelectedTenantId() ?? getStoredTenant()?.id ?? user?.tenant_id ??
      "current",
    user?.id ?? user?.username ?? "current",
  ] as const;
}
export const homeKeys = {
  state: () => ["hub", "home", ...homeScope()] as const,
  registry: () => ["hub", "home-widgets", ...homeScope()] as const,
  history: () => ["hub", "home-history", ...homeScope()] as const,
  defaults: (role: Role) =>
    ["hub", "home-defaults", ...homeScope(), role] as const,
};
export function dataKey(name: string, args: Record<string, unknown> = {}) {
  return ["hub", "canonical-data", ...homeScope(), name, args] as const;
}
export function useDataTool<T>(
  name: string,
  args: Record<string, unknown> = {},
  enabled = true,
) {
  return useQuery({
    queryKey: dataKey(name, args),
    queryFn: () => callDataTool<T>(name, args),
    enabled,
    staleTime: 30_000,
  });
}
export function useDataList<T>(
  name: string,
  args: Record<string, unknown> = {},
  enabled = true,
) {
  return useInfiniteQuery({
    queryKey: [...dataKey(name, args), "pages"],
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const result = await callDataTool<{ items: T[] }>(name, {
        limit: 5,
        ...args,
        ...(pageParam ? { cursor: pageParam } : {}),
      });
      if (!result.data || !Array.isArray(result.data.items)) {
        throw new Error("数据格式不正确，请刷新或回到业务页面核对");
      }
      return result;
    },
    getNextPageParam: (page) =>
      page.pagination?.hasMore && page.pagination.nextCursor
        ? page.pagination.nextCursor
        : undefined,
    enabled,
    staleTime: 30_000,
  });
}
export function listItems<T>(
  pages: DataToolResult<{ items: T[] }>[] | undefined,
) {
  return pages?.flatMap((page) => page.data.items) ?? [];
}
export function useWorkspaceMe() {
  return useDataTool<WorkspaceMe>("me");
}
export function message(error: unknown) {
  return error instanceof Error ? error.message : "读取失败，请重试";
}
export function dateLabel(
  value?: number | string | null,
  timezone = "Asia/Shanghai",
) {
  if (!value) return "未设置";
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? date.toLocaleString("zh-CN", {
      timeZone: timezone,
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
    : "日期待核对";
}
export function calendarRange(
  mode: "week" | "month",
  timezone: string,
  now = new Date(),
) {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-GB", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(now).map((p) => [p.type, p.value]),
  );
  const today = new Date(
    parts.year + "-" + parts.month + "-" + parts.day + "T12:00:00Z",
  );
  let from: Date, to: Date;
  if (mode === "month") {
    from = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
    to = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 1, 1));
  } else {
    from = new Date(today);
    from.setUTCDate(from.getUTCDate() - (from.getUTCDay() + 6) % 7);
    to = new Date(from);
    to.setUTCDate(to.getUTCDate() + 7);
  }
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
}
export function dayStart(timezone: string, now = new Date()) {
  const formatter = new Intl.DateTimeFormat("en-GB", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  });
  const parts = (at: Date) =>
    Object.fromEntries(
      formatter.formatToParts(at).map((part) => [part.type, part.value]),
    );
  const today = parts(now),
    target = Date.UTC(
      Number(today.year),
      Number(today.month) - 1,
      Number(today.day),
    ),
    date = new Date(target);
  // Resolve the timezone offset at the actual local midnight (including seasonal offset changes).
  for (let i = 0; i < 3; i++) {
    const local = parts(date),
      wall = Date.UTC(
        Number(local.year),
        Number(local.month) - 1,
        Number(local.day),
        Number(local.hour),
        Number(local.minute),
        Number(local.second),
      );
    date.setTime(date.getTime() + target - wall);
  }
  return date.toISOString();
}
